/**
 * Shared chat-message renderer used by both DesignView and NewChat.
 * Rendering rules (app-shell.css classes — strict match):
 *   - role 'user'        → `.msg.you`
 *   - role 'user' + file → `.msg.you.file > .msg-file-chip`
 *   - kind 'tool-step'   → `.msg.tool` (with inline ToolStepInline)
 *   - kind 'checkpoint'  → `.msg.agent` wrapping <CheckpointCard/>
 *   - kind 'agent_question' → `.msg.agent` wrapping <AgentQuestion/>
 *   - kind 'inline-edit-diff' → `.msg.agent` wrapping inline-edit card
 *   - kind 'hint'        → italic small `.msg.agent`
 *   - default            → `.msg.agent` markdown
 *
 * Hoisted out so NewChat (no design id, just welcome state) can reuse
 * the same DOM and CSS as DesignView without duplicating ~100 lines of
 * conditional rendering.
 *
 * Performance: ChatMessage component is wrapped in React.memo with a custom
 * equality check based on message object reference. This prevents re-rendering
 * settled messages on every SSE chunk — only the currently-streaming message
 * (which gets a new reference each chunk) re-renders.
 */
import React from 'react';
import RichMarkdown from './RichMarkdown';
import CheckpointCard from './CheckpointCard';
import QaReportCard from './QaReportCard';
// AgentQuestion is intentionally NOT imported anymore — the agent_question
// branch now renders as regular assistant text (see UX overhaul 2026-05).
// The component file remains for now; remove in a separate cleanup ticket.

// ── helpers ────────────────────────────────────────────────────────────
function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExt(name) {
  if (!name) return 'FILE';
  const m = name.match(/\.([a-z0-9]+)$/i);
  return (m ? m[1] : 'FILE').toUpperCase();
}

// Split a user-typed message from the `[Attached to this workflow]`
// metadata block that DesignView stitches in for the agent's benefit.
// The composer ships ONE string that has BOTH the user's words AND a
// machine-readable attachment list with raw s3Keys; the s3Key is for
// the agent, never for the user's eyes. Returns `{ visible, files }`
// where `visible` is the user-facing text and `files` is the
// extracted attachment list ([{label, name}]).
function splitAttachmentNote(raw) {
  if (typeof raw !== 'string' || !raw) return { visible: raw || '', files: [] };
  const marker = '\n\n[Attached to this workflow]\n';
  const i = raw.indexOf(marker);
  if (i === -1) return { visible: raw, files: [] };
  let visible = raw.slice(0, i).trim();
  // When DesignView had no user text it stitched the placeholder
  // "I attached file(s) — see below." — strip that too so the chip row
  // stands on its own.
  if (visible === 'I attached file(s) — see below.') visible = '';
  const block = raw.slice(i + marker.length);
  const files = [];
  for (const line of block.split('\n')) {
    const m = line.match(/^- ([^:]+): (.+?) \(s3Key: .+\)$/);
    if (m) files.push({ label: m[1].trim(), name: m[2].trim() });
  }
  return { visible, files };
}

// Humanized labels for tool names. Without these, the chat shows raw tool
// names like `read_extracted_brd` that look like a re-run of extraction
// and confuse users (the same row appears once for the summary call and
// again before propose_segments — both are reads from cache, not re-runs
// of the deterministic extractor). Keep in sync with ToolStep.js.
const TOOL_LABELS = {
  extract_brd: 'Extracting BRD',
  extract_data_mapping: 'Extracting data mapping',
  extract_in_parallel: 'Reading your documents',
  read_extracted_brd: 'Reading BRD',
  read_extracted_data_mapping: 'Reading data mapping',
  read_attachment: 'Reading attachment',
  read_brd_outline: 'Reading BRD outline',
  read_brd_section: 'Reading BRD section',
  read_brd_chunks: 'Reading BRD chunks',
  analyze_use_case_images: 'Analyzing images',
  select_use_cases: 'Selecting use cases',
  draft_plan: 'Planning the design',
  propose_foundations: 'Drafting foundations',
  reconcile_foundations: 'Reconciling foundations',
  propose_segments: 'Drafting segmentation',
  propose_channel_strategy: 'Drafting channel strategy',
  propose_decisioning: 'Drafting decisioning',
  propose_journey_flow: 'Drafting journey flow',
  propose_design_in_parallel: 'Designing all sections',
  propose_use_cases_in_parallel: 'Designing use cases',
  run_qa: 'Reviewing against best practices',
  export_document: 'Preparing the document',
  apply_block_edit: 'Applying your edit',
  record_hours_saved: 'Recording hours saved',
  ask_user: 'Waiting for input',
  set_stage: 'Setting stage',
  // KB lookups are an internal reasoning step — surface a neutral verb so
  // the live processing pill reads "Thinking…" instead of exposing the raw
  // tool name / "knowledge base" to the user.
  search_knowledge_base: 'Checking the AJO docs',
};

// Humanize unknown tool names: strip underscores, sentence-case.
function humanizeToolName(name) {
  if (!name) return 'Working';
  if (TOOL_LABELS[name]) return TOOL_LABELS[name];
  // Fallback: "some_new_tool" → "Some new tool"
  const words = name.split('_').filter(Boolean);
  if (words.length === 0) return 'Working';
  return words.map((w, i) => i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w).join(' ');
}

// Only these tools render as a visible "⏵ <label> ✓ done" bubble in the
// chat. Everything else (read_extracted_*, read_brd_section,
// analyze_use_case_images, read_attachment, set_stage, etc.) is silent —
// the agent's text narration carries the "what I'm doing" signal. Without
// this filter, every routine read/scan shows up as a dev-tool card that
// makes the chat feel mechanical and overwhelming. Stage-emitting tools
// stay visible because they're the actual progress markers users want.
const VISIBLE_TOOL_STEPS = new Set([
  'extract_brd',
  'extract_data_mapping',
  'extract_in_parallel',
  'propose_segments',
  'propose_channel_strategy',
  'propose_decisioning',
  'propose_journey_flow',
  'propose_design_in_parallel',
  'run_qa',
  'export_document',
]);

// Tool-result error codes the agent recovers from automatically (the model
// retries with corrected input on the next turn). These never render as a
// failure card — they're transient internal noise, not a user-facing error.
// Hard failures (e.g. `tool_failed`, `unknown_tool`) are NOT listed, so they
// still surface. Keep in sync with execute_tool() error codes in agent.py.
const SILENT_TOOL_ERRORS = new Set(['bad_tool_input']);

// ── inline tool-step card ──────────────────────────────────────────────
/**
 * Collapsed provenance footer on an assistant message — the KB docs that
 * grounded the answer (kb_sources SSE event). Web sources link out;
 * KB-bucket (s3://) sources render as document names.
 */
function KbSources({ sources }) {
  if (!Array.isArray(sources) || sources.length === 0) return null;
  const labelFor = (uri) => {
    try {
      if (/^https?:\/\//i.test(uri)) {
        const u = new URL(uri);
        const path = u.pathname.replace(/\/$/, '');
        const tail = path.split('/').slice(-2).join('/');
        return `${u.hostname}${tail ? ` — ${tail}` : ''}`;
      }
    } catch { /* fall through to basename */ }
    return String(uri).split('/').pop() || String(uri);
  };
  return (
    <details className="msg-kb-sources" style={{ marginTop: 6, fontSize: 11.5, color: 'var(--text-tertiary)' }}>
      <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
        Sources ({sources.length})
      </summary>
      <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
        {sources.map((s, i) => {
          const uri = typeof s === 'string' ? s : s?.uri || '';
          if (!uri) return null;
          const label = labelFor(uri);
          return (
            <li key={i} style={{ margin: '2px 0', overflowWrap: 'anywhere' }}>
              {/^https?:\/\//i.test(uri) ? (
                <a href={uri} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{label}</a>
              ) : (
                <span title={uri}>{label}</span>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

function ToolStepInline({ step }) {
  const tool = step?.tool || {};
  const status = tool.status === 'running' ? 'run' : tool.error ? 'err' : 'ok';
  const label = TOOL_LABELS[tool.name] || tool.name || 'tool';
  const summary = tool.error
    ? tool.error
    : tool.result?.error || (tool.result ? 'done' : '');

  return (
    <div className="tool-step-card">
      <span>⏵</span>
      <span>{label}</span>
      <span className={`tool-step-status ${status}`}>
        {status === 'run' ? '…' : status === 'ok' ? '✓' : '✕'}
        {summary ? ` ${summary}` : ''}
      </span>
    </div>
  );
}

// ── inline-edit diff card ──────────────────────────────────────────────
function InlineEditCard({ edit, onAccept, onReject, onRefine, onPreview }) {
  if (!edit) return null;
  const status = edit.status || 'pending';
  return (
    <div className="inline-edit-card">
      <div className="inline-edit-card-head">
        <span className="inline-edit-card-tag">
          ✦ Proposed edit · {edit.blockId || ''}
        </span>
        {onPreview && (
          <button className="btn btn-ghost" onClick={() => onPreview(edit.blockId)}>
            View
          </button>
        )}
      </div>
      <div className="inline-edit-card-diff">
        <div className="removed">{edit.oldText}</div>
        <div className="added">{edit.newText}</div>
      </div>
      {edit.rationale && (
        <div className="inline-edit-card-rationale">{edit.rationale}</div>
      )}
      <div className="inline-edit-card-actions">
        {status === 'pending' && (
          <>
            <button className="btn btn-blue" onClick={() => onAccept?.(edit)}>Accept</button>
            <button className="btn btn-outline" onClick={() => onReject?.(edit)}>Reject</button>
            {onRefine && (
              <button className="btn btn-ghost" onClick={() => onRefine?.(edit)}>Refine</button>
            )}
          </>
        )}
        {status === 'accepted' && <span style={{ fontSize: 12, color: 'var(--success-green)' }}>✓ Accepted</span>}
        {status === 'rejected' && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Rejected</span>}
      </div>
    </div>
  );
}

/**
 * Memoized chat message component. Re-renders only when the message object
 * reference changes (messages are updated immutably in DesignView — settled
 * messages keep the same reference, streaming messages get new refs each chunk).
 *
 * Custom areEqual: compare by message reference AND handlers reference. Since
 * handlers are recreated on every DesignView render, we need deep equality on
 * the handler object — but since all handlers are stable callbacks (defined
 * with useCallback or are plain functions), their identities are stable across
 * renders. We can safely compare handlers by reference after the parent hoists
 * them into a stable object.
 *
 * IMPORTANT: DesignView must hoist the handlers object into a stable reference
 * (useMemo or module-level constant) for this optimization to work. If handlers
 * is recreated every render, every message will re-render every time.
 */
const ChatMessage = React.memo(function ChatMessage({ message: m, index: idx, handlers = {} }) {
  const {
    onCheckpointContinue,
    onCheckpointSkip,
    onCheckpointRefine,
    onAgentQuestionAnswer,
    onAcceptInlineEdit,
    onRejectInlineEdit,
    onRefineInlineEdit,
    onPreviewInlineEditBlock,
    onSendMessage,
  } = handlers;

  // user with file attachment chip
  if (m.role === 'user' && m.attachment) {
    const att = m.attachment;
    return (
      <div className="msg you file" key={idx}>
        <div className="msg-file-chip">
          <div className="msg-file-chip-icon">{fileExt(att.name)}</div>
          <div>
            <div className="msg-file-chip-name" title={att.name}>{att.name}</div>
            <div className="msg-file-chip-meta">
              {formatBytes(att.size)}{att.pages ? ` · ${att.pages} pages` : ''}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (m.role === 'user') {
    const { visible, files } = splitAttachmentNote(m.content);
    if (!files.length) {
      return <div className="msg you" key={idx}>{visible}</div>;
    }
    return (
      <div className={`msg you${visible ? '' : ' attach-only'}`} key={idx}>
        {visible && <div>{visible}</div>}
        <div className="msg-attach-chips">
          {files.map((f, i) => (
            <span className="msg-attach-chip" key={`${f.name}-${i}`} title={f.name}>
              <span className="msg-attach-chip-icon">{fileExt(f.name)}</span>
              <span className="msg-attach-chip-name">{f.name}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }

  // tool-step (agent's tool call). Tool-step messages are no longer
  // inserted into the transcript — the live processing pill shows tool
  // progress. Historical scrollback rows (from before this change) are
  // hidden entirely. Return null for ALL tool-step rows.
  if (m.kind === 'tool-step') {
    return null;
  }
  // Legacy code below preserved for git history; unreachable after the
  // return above. Remove in a separate cleanup ticket.
  if (false && m.kind === 'tool-step') {
    const tname = m.tool?.name || '';
    const errCode = m.tool?.error || m.tool?.result?.error || '';
    const hasError = !!errCode && !SILENT_TOOL_ERRORS.has(errCode);
    if (!hasError && tname && !VISIBLE_TOOL_STEPS.has(tname)) {
      return null;
    }
    // The run_qa tool result carries the QA fields at the TOP LEVEL
    // (score / categoryScores / issues / pass) — NOT nested under `.qa`
    // (that's only the shape of the saved qa_review_v{N}.json file).
    // Accept either shape so the card renders for the live/persisted
    // flat result.
    const rawQa = (tname === 'run_qa' && m.tool?.result && typeof m.tool.result === 'object')
      ? (m.tool.result.qa || m.tool.result)
      : null;
    const qaData = rawQa && (rawQa.categoryScores || rawQa.score != null || Array.isArray(rawQa.issues))
      ? rawQa
      : null;
    // With a QA card, render as `.msg.agent` so it inherits the existing
    // full-width stretch for tool-step-card messages (a transparent block
    // column). Plain tool-steps stay a compact `.msg.tool` inline pill —
    // that inline-flex/nowrap pill style would otherwise collapse the card.
    if (qaData) {
      return (
        <div className="msg agent" key={idx}>
          <ToolStepInline step={m} />
          <QaReportCard qa={qaData} />
        </div>
      );
    }
    // Plain progress cards ("▶ Drafting design …", "▶ Extracting BRD ✓")
    // duplicate the live processing pill (DesignView shows the same verb +
    // spinner), so they no longer render. Errors still surface as a card so
    // a failed tool is visible; QA reports (handled above) also stay.
    if (hasError) {
      return (
        <div className="msg tool" key={idx}>
          <ToolStepInline step={m} />
        </div>
      );
    }
    return null;
  }

  // checkpoint card
  if (m.kind === 'checkpoint' && m.checkpoint) {
    const cp = m.checkpoint;
    const acted = cp.status === 'acted';
    const isParallelDesign = cp.stage === 'parallel_design';
    const isParallelExtraction = cp.stage === 'parallel_extraction';
    return (
      <div className="msg agent" key={idx}>
        <CheckpointCard
          stage={cp.stage}
          title={cp.title}
          summary={cp.summary}
          disabled={acted}
          onContinue={() => onCheckpointContinue?.(cp.stage, idx)}
          onRefine={() => onCheckpointRefine?.(cp.stage)}
          // R3 — no Skip on the parallel_design or parallel_extraction
          // combined cards. Pass undefined so CheckpointCard hides the
          // Skip button entirely.
          onSkip={
            isParallelDesign || isParallelExtraction
              ? undefined
              : () => onCheckpointSkip?.(cp.stage, idx)
          }
          // R1 — per-section refine on the parallel_design card sends
          // a single-stage refine (segmentation | channel | decisioning
          // | journey). The agent's system prompt routes that to ONLY
          // the matching propose_* tool, not the parallel composite.
          onRefineSection={
            isParallelDesign
              ? (section) => onCheckpointRefine?.(section)
              : undefined
          }
          sectionStatuses={cp.sectionStatuses}
          extractionSectionStatuses={cp.extractionSectionStatuses}
          onSendMessage={onSendMessage}
        />
      </div>
    );
  }

  // agent question — UX overhaul 2026-05: render as regular assistant
  // text + a prose options line instead of the old chip group. User
  // types the answer in the same composer they use for everything else.
  // The wire-level `agent_question` event stays on the SSE stream so
  // the backend resume protocol still works; only the chip UI is gone.
  if (m.kind === 'agent_question' && m.question) {
    const q = m.question;
    const opts = (q.options || []).map((o) => `\`${o}\``).join(' · ');
    const body = `${q.text || ''}${opts ? `\n\n**Options:** ${opts}` : ''}`;
    return (
      <div className="msg agent" key={idx}>
        <RichMarkdown content={body} />
      </div>
    );
  }

  // inline-edit diff
  if (m.kind === 'inline-edit-diff' && m.edit) {
    return (
      <div className="msg agent" key={idx}>
        <InlineEditCard
          edit={m.edit}
          onAccept={onAcceptInlineEdit}
          onReject={onRejectInlineEdit}
          onRefine={onRefineInlineEdit}
          onPreview={onPreviewInlineEditBlock}
        />
      </div>
    );
  }

  // hint (italic agent message)
  if (m.kind === 'hint') {
    return (
      <div
        className="msg agent"
        key={idx}
        style={{ fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}
      >
        {m.content}
      </div>
    );
  }

  // default agent text — rich markdown (GFM, math, syntax-highlighted code)
  return (
    <div className={`msg agent${m.error ? ' error' : ''}`} key={idx}>
      <RichMarkdown content={m.content || ''} />
      <KbSources sources={m.sources} />
    </div>
  );
}, (prevProps, nextProps) => {
  // Re-render only when the message object reference changes OR when handlers change.
  // Messages are updated immutably in DesignView (prev.map(m => m === target ? {...m} : m)),
  // so unchanged messages keep the same reference across renders.
  //
  // WARNING: If DesignView mutates messages in place anywhere, this optimization
  // breaks (settled messages won't update). The streaming code uses immutable
  // updates, so we're safe — but if a future refactor adds mutation, this will
  // silently fail. Diagnose by comparing specific fields instead of references:
  //   return prevProps.message.content === nextProps.message.content &&
  //          prevProps.message.role === nextProps.message.role &&
  //          prevProps.message.error === nextProps.message.error &&
  //          prevProps.message.kind === nextProps.message.kind &&
  //          ... (all relevant fields)
  return (
    prevProps.message === nextProps.message &&
    prevProps.handlers === nextProps.handlers &&
    prevProps.index === nextProps.index
  );
});

/**
 * Thin wrapper that returns a memoized ChatMessage component. This is the
 * public API that DesignView's messages.map(renderMessage) expects — a
 * function that takes (message, index, handlers) and returns a React element.
 *
 * The key prop is set here (not inside ChatMessage) so React's reconciliation
 * works correctly when messages are added/removed from the middle of the array.
 */
export function renderMessage(m, idx, handlers = {}) {
  return <ChatMessage key={idx} message={m} index={idx} handlers={handlers} />;
}

// Re-export RichMarkdown so DesignView's live-streaming bubble can use the
// same renderer (keeps streaming and finalized messages visually identical).
export { RichMarkdown };

// Re-export helpers for callers that need to render staged-file chips
// outside the message stream (e.g. composer-pill chips in DesignView).
export { formatBytes, fileExt };

// Re-export tool-name → friendly verb map. Consumed by the C6 processing
// pill in DesignView so the live indicator and the visible tool-step
// cards stay in sync. Single source of truth — keep here.
export { TOOL_LABELS, humanizeToolName };
